[
<template>
  <teleport to="body">
    <transition
      name="fade"
      appear>
      <div
        v-if="showModal"
        class="modal-bg"
        @click="closeModal"></div>
    </transition>
  </teleport>

  <teleport to="body">
    <transition
      name="fade"
      appear>
      <div
        v-if="showModal"
        class="modal">
        <div class="modal-content">
          <div
            v-if="openExternally"
            class="open-externally">
            <div class="text-container">
              <div class="text">
                <transition
                  name="speed-bump-content"
                  mode="out-in">
                  <div
                    :key="speedBumpContentKey"
                    v-html="activeSpeedBumpContent"></div>
                </transition>

                <transition name="speed-bump-buttons">
                  <div
                    v-if="showInitialButtons"
                    class="initial-buttons flex flex-col gap-0 lg:flex-row lg:flex-wrap lg:gap-x-16">
                    <template
                      v-for="(button, index) in speedBumpButtons"
                      :key="`button-${index}`">
                      <ButtonGlobal
                        v-if="button.buttonOnly"
                        :text="button.button.title"
                        :target="
                          validateUrl(button?.button.url) ? '_blank' : '_self'
                        "
                        :url="button?.button.url" />
                      <ButtonGlobal
                        v-else
                        :text="button.buttonText"
                        :func="
                          () => setSpeedBumpContent(button.extraContent)
                        " />
                    </template>
                  </div>
                </transition>
                <!-- <ButtonGlobal
                  text="Send an Email"
                  :target="validateUrl(urlValue) ? '_blank' : '_self'"
                  :url="urlValue" /> -->
              </div>
              <div
                class="close"
                @click="closeModal">
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, ref, watch, onBeforeMount } from 'vue'
import useSmoothScroll from '@/components/composables/useSmoothScroll'
import useUtils from '@/components/composables/useUtils'
import ButtonGlobal from '@/components/utility/ButtonGlobal.vue'
import { useStore } from '@/stores/main'

const { locoScroll } = useSmoothScroll()

import { useModal } from '@/components/composables/useModal.js'

const { showModal, openExternally, speedBumpValue, closeModal } = useModal()
const { isExternal } = useUtils()
const store = useStore()

const acf_options = ref(null)
const activeSpeedBumpContent = ref('')
const speedBumpContentKey = ref('default')
const showInitialButtons = ref(true)
const isEnabled = (value) => [true, 1, '1'].includes(value)

const activeSpeedBump = computed(
  () =>
    speedBumpValue.value ||
    acf_options.value?.speed_bump ||
    acf_options.value?.speedBump ||
    {}
)
const speedBumpButtons = computed(() => {
  const buttons =
    activeSpeedBump.value?.button_and_extra_content ??
    activeSpeedBump.value?.buttonAndExtraContent ??
    activeSpeedBump.value?.buttons_and_extra_content ??
    activeSpeedBump.value?.buttonsAndExtraContent ??
    []

  if (!Array.isArray(buttons)) return []

  return buttons.map((button) => ({
    buttonOnly: isEnabled(button?.button_only ?? button?.buttonOnly ?? false),
    button: button?.button || {},
    buttonText: button?.button_text ?? button?.buttonText ?? '',
    extraContent: button?.extra_content ?? button?.extraContent ?? '',
  }))
})

const setSpeedBumpContent = (content) => {
  activeSpeedBumpContent.value = content || ''
  speedBumpContentKey.value = `extra-${Date.now()}`
  showInitialButtons.value = false
}

const resetSpeedBumpContent = () => {
  activeSpeedBumpContent.value = activeSpeedBump.value?.content || ''
  speedBumpContentKey.value = 'default'
  showInitialButtons.value = Boolean(
    isEnabled(
      activeSpeedBump.value?.add_extra_content ??
        activeSpeedBump.value?.addExtraContent
    ) && speedBumpButtons.value.length
  )
}

const validateUrl = (url) => {
  if (!url || typeof url !== 'string') return false

  if (url.startsWith('mailto:')) {
    return false
  } else if (isExternal(url)) {
    return true
  } else {
    return false
  }
}

watch(showModal, (value) => {
  value ? locoScroll.value.stop() : locoScroll.value.start()

  if (value) {
    resetSpeedBumpContent()
  }
})

onBeforeMount(async () => {
  acf_options.value = await store.getAcfOptions()
  resetSpeedBumpContent()
})
</script>

<style>
@reference '@/assets/styles/main.css';
.modal-bg {
  @apply fixed top-0 bg-black/60 left-0 w-screen p-3 md:p-5 h-screen flex justify-center bg-blend-multiply items-center overflow-hidden z-[100];
}

.modal {
  @apply fixed top-0 left-0 w-screen p-3 md:p-5 h-screen flex justify-center bg-blend-multiply items-center overflow-hidden z-[101];

  .modal-content {
    @apply w-full p-1 min-h-[60%] flex flex-row z-10 justify-center items-stretch lg:min-h-[469px] lg:h-fit lg:w-fit;

    .open-externally {
      @apply h-auto flex;

      .text-container {
        @apply relative px-10 pb-10 pt-[60px] w-full flex flex-col max-w-[837px] justify-center lg:w-[1037px] lg:px-20 mx-auto rounded-xl overflow-hidden bg-white;

        .text {
          @apply w-full lg:max-w-full mx-auto flex flex-col;

          h2 {
            @apply header-5 normal-case mb-8;
          }

          p {
            @apply body mb-8;
          }

          a {
            @apply underline;
          }
        }
      }
    }

    .close {
      @apply flex flex-col justify-center items-center bg-transparent transition-all cursor-pointer rounded-full absolute right-2 w-[50px] h-[50px] border-balanced border top-2 pointer-events-auto z-30;

      span {
        @apply h-[2px] w-[30px] bg-balanced transition-colors block;

        &:first-child {
          @apply rotate-45 translate-y-[1px];
        }

        &:last-child {
          @apply -rotate-45;
        }
      }

      &:hover {
        @apply bg-balanced;
        span {
          @apply bg-white;
        }
      }
    }
  }
}

.speed-bump-content-enter-active,
.speed-bump-content-leave-active {
  @apply transition-all duration-300 ease-in-out;
}

.speed-bump-content-enter-from,
.speed-bump-content-leave-to {
  @apply opacity-0 translate-y-1;
}

.speed-bump-content-enter-to,
.speed-bump-content-leave-from {
  @apply opacity-100 translate-y-0;
}

.speed-bump-buttons-enter-active,
.speed-bump-buttons-leave-active {
  @apply transition-all duration-300 ease-in-out;
}

.speed-bump-buttons-enter-from,
.speed-bump-buttons-leave-to {
  @apply opacity-0 translate-y-1;
}

.speed-bump-buttons-enter-to,
.speed-bump-buttons-leave-from {
  @apply opacity-100 translate-y-0;
}
</style>
]
